/* eslint-disable react-hooks/exhaustive-deps */
import React, { forwardRef, useImperativeHandle, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import useSnackbar from 'src/hooks/useCustomSnackbar';
// @mui
import {
    Card,
    Typography,
    Divider,
    Box,
    Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
// components
import { Iconify } from '../../../../components';
// hooks
import { useSelector, dispatch } from '../../../../redux/store';
import { updateCompleteStatus, getAutoMatedMessages, AutoMatedMessageStatusUpdate, clearDentallyState } from '../../../../redux/slices/lead';
import { createDentallyAppointmentAsync } from '../../../../utils/dentallyApi';
import {
    AutomatedMessages,
} from '../../leadDetail';
import { dentallyInfoCardStyle } from '../../../../styles/DentallyBookingStyle';

// ── Inline styles matching the DentallyBookingStyle design system ───────────

const summaryCardStyle = {
    bgcolor: '#fff',
    borderRadius: 2.5,
    p: 2.5,
    mb: 2.5,
    border: '1px solid',
    borderColor: 'rgba(145,158,171,0.12)',
    boxShadow: '0 1px 4px rgba(145,158,171,0.08)',
};

const summaryLabelStyle = {
    fontSize: '11px',
    fontWeight: 700,
    color: 'text.disabled',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    mb: 0.5,
};

// ----------------------------------------------------------------

const MessageAndReminder = forwardRef(({ handleNext, type, row }, ref) => {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { enqueueSnackbar } = useSnackbar();
    const {
        newLeadDetail,
        createLead,
        autoMatedMessages,
        tableRowLead,
        isDentallyBooking,
        dentallySlot,
        dentallyPatientId,
        pendingDentallySync,
    } = useSelector((state) => state.lead);
    const [sentTasks, setSentTasks] = useState([]);
    const [upcommingTask, setUpcommingTask] = useState([]);
    const [failedTasks, setFailedTasks] = useState([]);
    const id = row?.lead_id || tableRowLead?.lead_id || newLeadDetail?.id;

    useEffect(() => {
        if (autoMatedMessages.results) {
            setSentTasks(autoMatedMessages?.results.filter((el) => el?.is_enqueued));
            setUpcommingTask(autoMatedMessages?.results.filter((el) => !el?.is_enqueued && !el?.is_cancelled));
            setFailedTasks(autoMatedMessages?.results.filter((el) => el?.is_cancelled));
        }
    }, [autoMatedMessages]);

    useEffect(() => {
        if (practiceId && type && type !== 'CreatLead') {
            dispatch(getAutoMatedMessages(practiceId, id));
        }
    }, [practiceId]);

    const onSubmit = useCallback(async (status) => {
        if (status?.toLowerCase?.() === 'finish') {
            // Fire async Dentally appointment creation if payment was confirmed
            if (isDentallyBooking && pendingDentallySync && dentallyPatientId) {
                if (pendingDentallySync === 'complete' && dentallySlot) {
                    const crmAppointmentId = createLead?.lead_appointment?.id || newLeadDetail?.lead_appointment?.id;
                    if (!crmAppointmentId) {
                        enqueueSnackbar('A saved CRM appointment is required before syncing to Dentally', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                        return;
                    }
                    try {
                        const res = await createDentallyAppointmentAsync(practiceId, {
                        lead_id: id,
                        crm_appointment_id: crmAppointmentId,
                        start_time: dentallySlot.start_time,
                        finish_time: dentallySlot.finish_time,
                        practitioner_id: dentallySlot.practitioner_id,
                        reason: dentallySlot.reason || 'Exam',
                        notes: dentallySlot.notes || 'Appointment created from DentalSem CRM',
                        patient_id: dentallyPatientId,
                        });
                        if (res?.ok || res?.status === 200 || res?.status === 201) {
                            enqueueSnackbar('Dentally appointment created successfully', { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                        } else {
                            enqueueSnackbar('Failed to create Dentally appointment \u2014 will retry later', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                            return;
                        }
                    } catch {
                        enqueueSnackbar('Failed to create Dentally appointment \u2014 will retry later', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                        return;
                    }
                }
                dispatch(clearDentallyState());
            }
            dispatch(updateCompleteStatus(true));
            handleNext(true);
            return;
        }
        handleNext(true);
    }, [
        createLead,
        dentallyPatientId,
        dentallySlot,
        enqueueSnackbar,
        handleNext,
        id,
        isDentallyBooking,
        newLeadDetail,
        pendingDentallySync,
        practiceId,
    ]);

    useImperativeHandle(
        ref,
        () => ({ submit: onSubmit }),
        [onSubmit]
    );

    const onUpdateStatus = (value, taskId) => {
        const detail = {
            'is_cancelled': value
        };
        dispatch(AutoMatedMessageStatusUpdate(practiceId, id, taskId, detail, handleSuccess));
    };

    const handleSuccess = (data) => {
        enqueueSnackbar(data.message || '', {
            variant: data.success ? 'success' : 'error',
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
            }
        });
    };

    const treatmentName = newLeadDetail?.lead_treatment?.treatment;
    const appointmentDateRaw = newLeadDetail?.lead_appointment?.datetime;
    const appointmentDate = appointmentDateRaw
        ? moment(appointmentDateRaw.replace?.('.0Z', ''), ['DD-MM-YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm:ss', moment.ISO_8601]).format('DD MMM YYYY hh:mm A')
        : null;
    const isFollowUp = type && type !== 'CreatLead';

    return (
        <Card sx={{ p: 3, boxShadow: 'none' }}>
            <Typography variant='h4' sx={{ mb: 1 }}>
                Messages & reminders
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2.5 }}>
                Review your booking details before finishing.
            </Typography>

            <Divider sx={{ mb: 2.5 }} />

            {/* Dentally sync status */}
            {isDentallyBooking && pendingDentallySync && (
                <Box sx={{ ...dentallyInfoCardStyle(pendingDentallySync === 'complete' ? 'success' : 'warning'), mb: 2.5 }}>
                    <Iconify
                        icon={pendingDentallySync === 'complete' ? 'mdi:check-circle' : 'mdi:clock-alert-outline'}
                        width={22}
                        sx={{ color: pendingDentallySync === 'complete' ? 'success.main' : 'warning.main', flexShrink: 0 }}
                    />
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        {pendingDentallySync === 'complete'
                            ? 'Dentally appointment will be created when you finish'
                            : 'Dentally sync reminder has been set \u2014 appointment will be synced later'}
                    </Typography>
                </Box>
            )}

            {/* ── Booking Summary ────────────────────────────────────────── */}
            <Box sx={summaryCardStyle}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Box sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <Iconify icon="mdi:check-bold" width={16} sx={{ color: 'success.main' }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Booking summary
                    </Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={summaryLabelStyle}>Treatment</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {treatmentName || <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>Not selected</Box>}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={summaryLabelStyle}>Appointment date & time</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', textTransform: 'capitalize' }}>
                            {appointmentDate || <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>Not scheduled</Box>}
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* ── Automated Messages (follow-up flow only) ───────────────── */}
            {isFollowUp && <AutomatedMessages sentTasks={sentTasks} upcommingTask={upcommingTask} failedTasks={failedTasks} onUpdateStatus={onUpdateStatus} />}
        </Card>
    );
});

MessageAndReminder.propTypes = {
    handleNext: PropTypes.func,
    type: PropTypes.string,
    row: PropTypes.any
};
export default MessageAndReminder;
